import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { tecnicos, clusters } from '../lib/api.js';
import Avatar from '../components/Avatar.jsx';
import TecnicosTabs from '../components/TecnicosTabs.jsx';

const COR_UNIDADE = {
  Matriz:       '#1e6fc8',
  Serra:        '#16a34a',
  Praia:        '#ea580c',
  'Ilha Sul':   '#9333ea',
  'Ilha Leste': '#dc2626',
};

export default function TecnicosPage() {
  const [lista, setLista] = useState([]);
  const [clustersList, setClustersList] = useState([]);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroUnidade, setFiltroUnidade] = useState('todas');
  const [filtroStatus, setFiltroStatus] = useState('ativos'); // ativos | todos | inativos

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    try {
      const [t, c] = await Promise.all([
        tecnicos.list({ limit: 200 }),
        clusters.list(),
      ]);
      setLista(t.data || []);
      setClustersList(c.data || []);
    } catch (e) { setErro(e.uiMessage); }
  }

  const filtrados = useMemo(() => {
    let r = lista;
    if (filtroStatus === 'ativos')   r = r.filter(t => t.ativo);
    if (filtroStatus === 'inativos') r = r.filter(t => !t.ativo);
    if (filtroUnidade !== 'todas')   r = r.filter(t => t.unidade_negocio === filtroUnidade);
    if (busca.trim()) {
      const q = busca.trim().toLowerCase();
      r = r.filter(t =>
        (t.nome || '').toLowerCase().includes(q) ||
        (t.apelido || '').toLowerCase().includes(q) ||
        (t.cidade || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [lista, busca, filtroUnidade, filtroStatus]);

  // Agrupar por unidade
  const grupos = useMemo(() => {
    const g = {};
    for (const t of filtrados) {
      const k = t.unidade_negocio || '(sem unidade)';
      (g[k] = g[k] || []).push(t);
    }
    return g;
  }, [filtrados]);

  const ordemUnidades = ['Matriz','Serra','Praia','Ilha Sul','Ilha Leste','(sem unidade)'];
  const gruposOrdenados = ordemUnidades.filter(u => grupos[u]?.length);

  return (
    <div className="stack">
      <TecnicosTabs />
      <div className="row between">
        <h2 style={{ margin: 0 }}>Técnicos</h2>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {filtrados.length} de {lista.length}
        </span>
      </div>

      {erro && <div className="card error">{erro}</div>}

      {/* Filtros */}
      <div className="card" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="🔍 Buscar nome, apelido ou cidade..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ width: 240 }}
          />
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <Chip ativo={filtroStatus === 'ativos'}   onClick={() => setFiltroStatus('ativos')}>Ativos</Chip>
            <Chip ativo={filtroStatus === 'todos'}    onClick={() => setFiltroStatus('todos')}>Todos</Chip>
            <Chip ativo={filtroStatus === 'inativos'} onClick={() => setFiltroStatus('inativos')}>Inativos</Chip>
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            <Chip ativo={filtroUnidade === 'todas'} onClick={() => setFiltroUnidade('todas')}>
              Todas unidades
            </Chip>
            {clustersList.map(c => (
              <Chip key={c.id}
                ativo={filtroUnidade === c.unidade_negocio}
                cor={c.cor_hex}
                onClick={() => setFiltroUnidade(c.unidade_negocio)}>
                {c.unidade_negocio || c.nome}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      {/* Agrupamento por unidade */}
      {gruposOrdenados.map(unidade => (
        <div key={unidade} className="stack" style={{ gap: '0.5rem' }}>
          <h3 style={{
            margin: 0,
            color: COR_UNIDADE[unidade] || '#374151',
            fontSize: '1rem',
            borderLeft: `4px solid ${COR_UNIDADE[unidade] || '#9ca3af'}`,
            paddingLeft: '0.6rem',
          }}>
            {unidade} <span className="muted" style={{ fontWeight: 'normal', fontSize: '0.8rem' }}>· {grupos[unidade].length}</span>
          </h3>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Apelido</th>
                  <th>Nome completo</th>
                  <th>Cidade</th>
                  <th>Jornada</th>
                  <th>OS/dia</th>
                  <th>Coef.</th>
                  <th>Skills</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {grupos[unidade].map(t => (
                  <tr key={t.id} style={{ opacity: t.ativo ? 1 : 0.5 }}>
                    <td><Avatar nome={t.apelido || t.nome} id={t.id} fotoUrl={t.foto_url} size={36}/></td>
                    <td>
                      <Link to={`/tecnicos/${t.id}`} style={{ textDecoration: 'none', color: 'inherit' }}
                        title="Clique pra editar">
                        <strong style={{ borderBottom: '1px dashed transparent', transition: 'border-color 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderBottomColor = 'currentColor'}
                          onMouseLeave={e => e.currentTarget.style.borderBottomColor = 'transparent'}>
                          {t.apelido || t.nome.split(' ')[0]}
                        </strong>
                      </Link>
                      {t.is_terceirizado && <span title="Terceirizado / PJ" style={{ marginLeft: 6, fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '0px 4px', borderRadius: 3 }}>PJ</span>}
                      {!t.ativo && <span style={{ marginLeft: 6, fontSize: '0.7rem' }}>(inativo)</span>}
                    </td>
                    <td className="muted">{t.nome}</td>
                    <td>{t.cidade}</td>
                    <td>{t.horario_inicio?.slice(0,5)}-{t.horario_fim?.slice(0,5)}</td>
                    <td>{t.capacidade_diaria_os}</td>
                    <td>{Number(t.coeficiente_velocidade).toFixed(2)}</td>
                    <td>
                      <span className="muted" style={{ fontSize: '0.7rem' }}>
                        {(t.especializacoes || []).length} skills
                      </span>
                    </td>
                    <td>
                      <Link to={`/tecnicos/${t.id}`}
                        className="btn small secondary"
                        style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}>
                        Editar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {gruposOrdenados.length === 0 && (
        <div className="card muted" style={{ padding: '2rem', textAlign: 'center' }}>
          Nenhum técnico encontrado.
        </div>
      )}
    </div>
  );
}

function Chip({ children, ativo, cor, onClick }) {
  const customCor = cor && ativo;
  return (
    <button onClick={onClick}
      className={`chip-base${ativo ? ' active' : ''}`}
      style={customCor ? { background: cor, borderColor: cor, color: '#fff' } : undefined}>
      {children}
    </button>
  );
}
